// File: /opt/dev_environment/assets/workspace/app-init/src/pages/StartPage/StartPage.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVocabulary } from '../../context/VocabularyContext';
import { LEITNER_COMPARTMENTS, TRAINING_MODES } from '../../constants/leitner';
import DirectionSelector from '../../components/DirectionSelector/DirectionSelector';
import ModeSwitcher from '../../components/ModeSwitcher/ModeSwitcher';
import SwipeableCard from '../../components/SwipeableCard/SwipeableCard';
import CompartmentVisualization3D from '../../components/CompartmentVisualization3D/CompartmentVisualization3D';
import './StartPage.css';

function StartPage() {
  const navigate = useNavigate();
  const {
    cards,
    verbCards,
    settings,
    trainingMode,
    selectedCompartment,
    shuffleActive,
    shufflePromptMessage,
    currentCardIndex,
    allReviewed,
    isLoaded,
    loadError,
    setDirection,
    setTrainingMode,
    selectCompartment,
    toggleShuffle,
    getCompartmentCounts,
    getDueCards,
    getCardsForSelectedCompartment,
    getCardErrorRate,
    recordReview,
    graduateCard,
    advanceCardIndex,
    autoAdvanceCompartment,
    findNextCompartmentWithCards,
    setAllReviewed,
    getProgress,
    getActiveCardsForMode,
    markCardReviewed,
  } = useVocabulary();

  const [promptVisible, setPromptVisible] = useState(false);
  const [cardKey, setCardKey] = useState(0);

  const counts = getCompartmentCounts();
  const compartmentCards = getCardsForSelectedCompartment();
  const dueCards = getDueCards();
  const progress = getProgress();
  const activeCards = getActiveCardsForMode();
  const isVerbMode = trainingMode === TRAINING_MODES.VERBEN;
  const cardType = isVerbMode ? 'verb' : 'vocabulary';

  const ShuffleIcon = () => (
    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" version="1.1" viewBox="0 0 16 16" height="200px" width="200px" xmlns="http://www.w3.org/2000/svg"><path d="M12 11h-1.586l-2.5-2.5 2.5-2.5h1.586v2.5l3.5-3.5-3.5-3.5v2.5h-2c-0.265 0-0.52 0.105-0.707 0.293l-2.793 2.793-2.793-2.793c-0.188-0.188-0.442-0.293-0.707-0.293h-3v2h2.586l2.5 2.5-2.5 2.5h-2.586v2h3c0.265 0 0.52-0.105 0.707-0.293l2.793-2.793 2.793 2.793c0.188 0.188 0.442 0.293 0.707 0.293h2v2.5l3.5-3.5-3.5-3.5v2.5z"></path></svg>
  );

  const currentCard = useMemo(() => {
    if (allReviewed) return null;

    if (selectedCompartment !== null) {
      if (compartmentCards.length > 0 && currentCardIndex < compartmentCards.length) {
        return compartmentCards[currentCardIndex];
      }
      return null;
    }

    return dueCards.length > 0 ? dueCards[0] : null;
  }, [selectedCompartment, compartmentCards, currentCardIndex, dueCards, allReviewed]);

  const cardStats = useMemo(() => {
    if (!currentCard) return null;
    const stats = getCardErrorRate(currentCard.id);
    return {
      correct: stats.total - stats.errors,
      wrong: stats.errors,
      total: stats.total,
    };
  }, [currentCard, getCardErrorRate]);

  const getEmptyBorderColor = () => {
    if (selectedCompartment !== null) {
      return LEITNER_COMPARTMENTS[selectedCompartment]?.color || 'var(--color-border)';
    }
    if (allReviewed) {
      return 'var(--color-success)';
    }
    return 'var(--color-border)';
  };

  useEffect(() => {
    if (shufflePromptMessage) {
      setPromptVisible(true);
      const timer = setTimeout(() => {
        setPromptVisible(false);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [shufflePromptMessage]);

  const handleAdvanceToNext = useCallback(() => {
    if (!selectedCompartment) return;

    const currentActiveCards = isVerbMode ? verbCards : cards;
    const remainingCards = currentActiveCards.filter(
      (c) => c.compartment === selectedCompartment
    );

    if (remainingCards.length === 0) {
      const nextComp = findNextCompartmentWithCards(selectedCompartment);
      if (nextComp !== null && nextComp !== selectedCompartment) {
        autoAdvanceCompartment(nextComp);
      } else {
        setAllReviewed();
      }
    }

    setCardKey((prev) => prev + 1);
  }, [
    selectedCompartment,
    cards,
    verbCards,
    isVerbMode,
    findNextCompartmentWithCards,
    autoAdvanceCompartment,
    setAllReviewed,
  ]);

  const handleSwipeRight = useCallback(async (card) => {
    if (!card) return;

    try {
      if (card.compartment === 5) {
        await recordReview(card.id, true, settings.queryDirection, cardType);
        await graduateCard(card.id, cardType);
        setTimeout(() => {
          handleAdvanceToNext();
        }, 50);
        return;
      }

      await recordReview(card.id, true, settings.queryDirection, cardType);
      setTimeout(() => {
        handleAdvanceToNext();
      }, 50);
    } catch (error) {
      console.error('Swipe right failed:', error);
    }
  }, [recordReview, graduateCard, settings.queryDirection, handleAdvanceToNext, cardType]);

  const handleSwipeLeft = useCallback(async (card) => {
    if (!card) return;
    try {
      await recordReview(card.id, false, settings.queryDirection, cardType);
      markCardReviewed(card.id);
      setTimeout(() => {
        handleAdvanceToNext();
      }, 50);
    } catch (error) {
      console.error('Swipe left failed:', error);
    }
  }, [recordReview, settings.queryDirection, handleAdvanceToNext, cardType, markCardReviewed]);

  const handleCompartmentClick = (num) => {
    const compartmentNum = parseInt(num, 10);
    if (selectedCompartment === compartmentNum) {
      selectCompartment(null);
    } else {
      selectCompartment(compartmentNum);
    }
    setCardKey((prev) => prev + 1);
  };

  const getEmptyMessage = () => {
    if (allReviewed) {
      return 'Alle Karten wurden überprüft! Gut gemacht!';
    }
    if (selectedCompartment !== null && compartmentCards.length === 0) {
      return `Keine Karten in Fach ${selectedCompartment}`;
    }
    if (activeCards.length === 0) {
      if (isVerbMode) {
        return 'Keine Verbkarten vorhanden.\nErstelle Karten auf der Verben-Seite!';
      }
      return 'Noch keine Vokabeln vorhanden.\nFüge deine erste Karte hinzu!';
    }
    return 'Keine Karten zur Wiederholung fällig.\nGut gemacht! 🎉';
  };

  const getEmptyIcon = () => {
    if (allReviewed) return '🎉';
    if (selectedCompartment !== null && compartmentCards.length === 0) return '📭';
    if (activeCards.length === 0 && isVerbMode) return '🔤';
    return '🗃️';
  };

  const getEmptyTitle = () => {
    if (allReviewed) return 'Geschafft!';
    if (selectedCompartment !== null && compartmentCards.length === 0) return `Fach ${selectedCompartment} ist leer`;
    return '';
  };

  // Show loading state
  if (!isLoaded) {
    return (
      <div className="start-page">
        <div className="swipeable-card-completion" style={{ borderColor: 'var(--color-border)' }}>
          <span className="swipeable-card-completion__icon" role="img" aria-hidden="true">⏳</span>
          <h3 className="swipeable-card-completion__title">Laden…</h3>
          <p className="swipeable-card-completion__text">Daten werden geladen</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (loadError) {
    return (
      <div className="start-page">
        <div className="swipeable-card-completion" style={{ borderColor: 'var(--color-error)' }}>
          <span className="swipeable-card-completion__icon" role="img" aria-hidden="true">❌</span>
          <h3 className="swipeable-card-completion__title">Fehler beim Laden</h3>
          <p className="swipeable-card-completion__text">{loadError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="start-page">

      <CompartmentVisualization3D counts={counts} />

      <ModeSwitcher
        value={trainingMode}
        onChange={setTrainingMode}
      />

      <div className="start-page__direction-section">
        <DirectionSelector
          value={settings.queryDirection}
          onChange={setDirection}
        />
      </div>

      <div className="leitner-boxes-row" role="list" aria-label="Leitner-Fächer">
        {Object.entries(LEITNER_COMPARTMENTS).map(([num, config]) => {
          const compartmentNum = parseInt(num, 10);
          const isActive = selectedCompartment === compartmentNum;
          return (
            <button
              key={num}
              className={`leitner-box leitner-box--${num}${isActive ? ' leitner-box--active' : ''}`}
              role="listitem"
              aria-label={`${config.label}: ${counts[num] || 0} Karten${isActive ? ', ausgewählt' : ''}`}
              aria-pressed={isActive}
              onClick={() => handleCompartmentClick(num)}
              type="button"
            >
              <span className="leitner-box__count">{counts[num] || 0}</span>
              <span className="leitner-box__label">Fach {num}</span>
            </button>
          );
        })}

        <button
          className={`shuffle-btn${shuffleActive ? ' shuffle-btn--active' : ''}`}
          type="button"
          onClick={toggleShuffle}
          aria-label={shuffleActive ? 'Mischen deaktivieren' : 'Karten mischen'}
          aria-pressed={shuffleActive}
          style={selectedCompartment ? {
            borderColor: LEITNER_COMPARTMENTS[selectedCompartment]?.color,
            ...(shuffleActive ? {
              backgroundColor: ({
                1: 'rgba(229, 57, 53, 0.1)',
                2: 'rgba(255, 143, 0, 0.1)',
                3: 'rgba(253, 216, 53, 0.1)',
                4: 'rgba(102, 187, 106, 0.1)',
                5: 'rgba(66, 165, 245, 0.1)',
              })[selectedCompartment] || undefined,
              borderWidth: '3px',
            } : {}),
          } : undefined}
        >
          <span className="shuffle-btn__icon" aria-hidden="true">
            <ShuffleIcon />
          </span>
        </button>
      </div>

      {promptVisible && shufflePromptMessage && (
        <div className="shuffle-prompt" role="alert">
          {shufflePromptMessage}
        </div>
      )}

      {currentCard ? (
        <SwipeableCard
          key={`${currentCard.id}-${cardKey}`}
          card={currentCard}
          cardStats={cardStats}
          direction={settings.queryDirection}
          onSwipeRight={handleSwipeRight}
          onSwipeLeft={handleSwipeLeft}
        />
      ) : (
        <div
          className="swipeable-card-completion"
          style={{ borderColor: getEmptyBorderColor() }}
        >
          <span className="swipeable-card-completion__icon" role="img" aria-hidden="true">
            {getEmptyIcon()}
          </span>
          <h3 className="swipeable-card-completion__title">
            {getEmptyTitle()}
          </h3>
          <p className="swipeable-card-completion__text">
            {getEmptyMessage()}
          </p>
        </div>
      )}

    </div>
  );
}

export default StartPage;